import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Edit2, Trash2, Shield, X, Search, Check, UserX, UserCheck, Loader2 } from 'lucide-react';
import { adminApi } from '../../services/api';
import { formatDate } from '../../utils/formatters';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRoles, setEditingRoles] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        adminApi.listUsers(),
        adminApi.listRoles(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (err) {
      console.error('Failed to load admin data:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = users.filter((u) =>
    !search ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddUser = async (formData) => {
    setSaving(true);
    try {
      await adminApi.createUser(formData);
      setShowAddModal(false);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async (userId, data) => {
    setSaving(true);
    try {
      await adminApi.updateUser(userId, data);
      setEditingUser(null);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRoles = async (userId, roleIds) => {
    setSaving(true);
    try {
      await adminApi.updateUserRoles(userId, roleIds);
      setEditingRoles(null);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    if (!confirm(`${user.is_active ? 'Deactivate' : 'Activate'} ${user.email}?`)) return;
    try {
      await adminApi.updateUser(user.user_id, {
        firstName: user.first_name,
        lastName: user.last_name,
        isActive: !user.is_active,
      });
      await loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`Permanently delete ${user.email}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteUser(user.user_id);
      await loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-amber-500" />
            User Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} users total</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 font-medium text-sm"
        >
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">User</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Roles</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Last Login</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {user.first_name} {user.last_name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.length > 0 ? user.roles.map((r) => (
                        <span
                          key={r.role_key}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            r.role_key === 'sysadmin'
                              ? 'bg-red-100 text-red-700'
                              : r.role_key === 'admin'
                              ? 'bg-purple-100 text-purple-700'
                              : r.role_key === 'analyst'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {r.role_name}
                        </span>
                      )) : (
                        <span className="text-xs text-gray-400 italic">No roles</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditingRoles(user)}
                        className="p-1.5 rounded hover:bg-amber-50 text-gray-400 hover:text-amber-600"
                        title="Edit roles"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditingUser(user)}
                        className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600"
                        title="Edit user"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                        title={user.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal
          roles={roles}
          saving={saving}
          onSave={handleAddUser}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          saving={saving}
          onSave={(data) => handleUpdateUser(editingUser.user_id, data)}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* Edit Roles Modal */}
      {editingRoles && (
        <EditRolesModal
          user={editingRoles}
          allRoles={roles}
          saving={saving}
          onSave={(roleIds) => handleUpdateRoles(editingRoles.user_id, roleIds)}
          onClose={() => setEditingRoles(null)}
        />
      )}
    </div>
  );
}

// ─── Modals ─────────────────────────────────────────────

function ModalWrapper({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

function AddUserModal({ roles, saving, onSave, onClose }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);

  const toggleRole = (roleId) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  return (
    <ModalWrapper title="Add New User" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            placeholder="user@sunnybenefits.com"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Assign Roles</label>
          <div className="space-y-2">
            {roles.map((r) => (
              <label key={r.role_id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRoles.includes(r.role_id)}
                  onChange={() => toggleRole(r.role_id)}
                  className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                />
                <span className="text-sm text-gray-700">{r.role_name}</span>
                <span className="text-xs text-gray-400">({r.role_key})</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={() => onSave({ email, firstName, lastName, roleIds: selectedRoles })}
            disabled={!email || saving}
            className="px-4 py-2 text-sm bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Add User'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}

function EditUserModal({ user, saving, onSave, onClose }) {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');

  return (
    <ModalWrapper title="Edit User" onClose={onClose}>
      <div className="space-y-4">
        <div className="text-sm text-gray-500">{user.email}</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={() => onSave({ firstName, lastName, isActive: user.is_active })}
            disabled={saving}
            className="px-4 py-2 text-sm bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}

function EditRolesModal({ user, allRoles, saving, onSave, onClose }) {
  const [selectedRoles, setSelectedRoles] = useState(
    user.roles.map((r) => r.role_id)
  );

  const toggleRole = (roleId) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  return (
    <ModalWrapper title={`Roles — ${user.email}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="space-y-2">
          {allRoles.map((r) => (
            <label key={r.role_id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedRoles.includes(r.role_id)}
                onChange={() => toggleRole(r.role_id)}
                className="mt-0.5 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">{r.role_name}</div>
                <div className="text-xs text-gray-400">{r.description}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={() => onSave(selectedRoles)}
            disabled={saving}
            className="px-4 py-2 text-sm bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Update Roles'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}
